import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Button, Table, Card } from 'react-bootstrap'
import Message from '../components/Message'
import ToastMessage from '../components/ToastMessage'
import Loader from '../components/Loader'
import S3ZipperDownload from '../../media_module/components/S3ZipperDownload'
import AssignParticipant from '../components/AssignParticipant'
import ViewOptionalTreatments from '../components/ViewOptionalTreatments'
import EmaSchedule from '../../ema_module/components/EmaSchedule'
import DiaryAxTask from '../../diary_ax_module/components/DiaryAxTask'
import ScheduledJitaiTask from '../../jitai_module/components/ScheduledJitaiTask'
import { ActiveRulesViewer, EvaluateCustomImplementation } from '../../jitai_module/components/DecisionRuleTester'
import { Last24hActivity } from '../../jitai_module/components/Last24hActivity'

import ParticipantFilters from '../components/ParticipantFilters'
import CreateParticipant from '../components/CreateParticipant'

import { getPartiList, downloadTextResp } from '../actions/participantActions'
import {
  DOWNLOAD_TEXT_RESP_RESET,
  PARTI_LIST_RESET,
} from '../constants/participantConstants'
import { STUDY_DETAILS_RESET } from '../../study_module/constants/studyConstants'

import getDateTimeString from '../utils/getDateTimeString'

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential'
import { getAllAccess } from '../../access_module/actions/accessActions'
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants'
import EMA_OR_EMI from '../../ema_or_emi'
import ViewFitbitSleepLog from '../components/ViewFitbitSleepLog'
import SmartWatchConnection from '../components/SmartWatchConnection'
import ScheduleSleepWakeTime from '../components/ScheduleSleepWakeTime'

import moment from 'moment-timezone'

const ParticipantListScreen = ({ history, match }) => {
  const studyId = match.params.studyId

  // For toast message
  const [toastMessages, setToastMessages] = useState([])

  // For participant filters
  const [currentStage, setCurrentStage] = useState('')
  const [currentGroup, setCurrentGroup] = useState('')
  const [studyCode, setStudyCode] = useState('')
  const [studyName, setStudyName] = useState('')

  // Settings-set selection (primary filter, above Create). undefined => study default set.
  const [selectedSettingsSetId, setSelectedSettingsSetId] = useState(undefined)
  const [settingsSetOptions, setSettingsSetOptions] = useState([])

  // For download images
  const [errorDownloadImgResp, setErrorDownloadImgResp] = useState([])

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    )
  }

  const dispatch = useDispatch()

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const partiList = useSelector((state) => state.partiList)
  const {
    participantsData,
    error: errorGetList,
    loading: loadingGetList,
    success: successGetList,
  } = partiList

  const downloadTextRespState = useSelector((state) => state.downloadTextResp)
  const {
    error: errorDownloadTextResp,
    loading: loadingDownloadTextResp,
    success: successDownloadTextResp,
  } = downloadTextRespState

  const allAccess = useSelector((state) => state.allAccess)
  const {
    loading: laodingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess

  // For Fiona's CBTi research only
  const getAutoGroupAssignmentDateTimeForCuhkCBTi = (scheduledDateTime) => {
    if (!scheduledDateTime) {
      return 'N/A'
    }

    const scheduledTimeInHK = moment.tz(
      scheduledDateTime,
      'YYYY-MM-DD HH:mm',
      'Hongkong'
    )

    const checkRangeStart = scheduledTimeInHK.clone().startOf('day')
    const checkRangeEnd = scheduledTimeInHK
      .clone()
      .startOf('day')
      .add(2, 'hours')

    let firstScheduleDateTime

    if (scheduledTimeInHK.isBetween(checkRangeStart, checkRangeEnd)) {
      firstScheduleDateTime = checkRangeEnd
    } else {
      firstScheduleDateTime = checkRangeEnd.clone().add(1, 'days')
    }

    const now = moment().tz('Hongkong')
    if (firstScheduleDateTime > now) {
      return firstScheduleDateTime.format('YYYY-MM-DD hh:mm A')
    } else {
      const timeIfPerformToday = now.clone().startOf('day').add(2, 'hours')
      if (now > timeIfPerformToday) {
        return timeIfPerformToday.add(1, 'days').format('YYYY-MM-DD hh:mm A')
      } else {
        return timeIfPerformToday.format('YYYY-MM-DD hh:mm A')
      }
    }
  }

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: PARTI_LIST_RESET })
      history.push('/login')
    } else {
      if (
        !loadingGetList &&
        !errorGetList &&
        !successGetList &&
        currentStage.length !== 0 &&
        currentGroup.length !== 0
      ) {
        // Put the reset for getting all access just before loading the main content again
        dispatch({ type: GET_ALL_ACCESS_RESET })
        dispatch(getPartiList(studyId, currentStage, currentGroup))
      }
      if (successDownloadTextResp) {
        dispatch({ type: DOWNLOAD_TEXT_RESP_RESET })
      }
      if (!laodingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess())
      }
    }
  }, [
    dispatch,
    history,
    userInfo,
    logout,
    loadingGetList,
    errorGetList,
    successGetList,
    laodingGetAllAccess,
    errorGetAllAccess,
    successGetAllAccess,
    successDownloadTextResp,
    studyId,
    participantsData,
    currentStage,
    currentGroup,
  ])

  const downloadTextData = async () => {
    dispatch(downloadTextResp(studyId, currentStage, currentGroup))
  }

  const getStageStartTime = (participation) => {
    const enrolmentTime =
      participation.enrolment_time[participation.enrolment_time.length - 1]
    return getDateTimeString(enrolmentTime.start_datetime)
  }

  //selectedGroupId could be equal to 'all', which represents all groups
  const handleFiltersUpdated = (selectedStageId, selectedGroupId) => {
    dispatch({ type: PARTI_LIST_RESET })
    setCurrentStage(selectedStageId)
    setCurrentGroup(selectedGroupId)
  }

  const onStudyLoaded = (study) => {
    setStudyCode(study.study_code)
    setStudyName(study.chin_name)
    setSettingsSetOptions(study.settings_set_options || [])
  }

  // The effective set drives the filters/create/assign. Before the user picks one, use
  // whichever set the loaded study resolved to (the default set on first open).
  const defaultSettingsSetOption = settingsSetOptions.find((o) => o.is_default)
  const effectiveSettingsSetId =
    selectedSettingsSetId ||
    (defaultSettingsSetOption && String(defaultSettingsSetOption._id)) ||
    ''

  // Switching settings set is a full list reload for that set: clear the current stage/
  // group selection and the loaded study tree so ParticipantFilters re-seeds stage 1 of
  // the new set, then re-fetches participants.
  const handleSettingsSetChange = (newSetId) => {
    if (String(newSetId) === String(effectiveSettingsSetId)) return
    setSelectedSettingsSetId(newSetId)
    setCurrentStage('')
    setCurrentGroup('')
    dispatch({ type: PARTI_LIST_RESET })
    dispatch({ type: STUDY_DETAILS_RESET })
  }

  const onAssignParticipantSuccess = () => {
    setToastMessages((toastMessages) => [
      ...toastMessages,
      {
        uid: new Date().valueOf(),
        variant: 'info',
        message: 'Participant has been assigned to the designated stage',
      },
    ])
  }

  const onAmiConnectionChange = () => {
    if (currentStage.length !== 0 && currentGroup.length !== 0) {
      dispatch(getPartiList(studyId, currentStage, currentGroup))
    }
  }

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  }

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          height: '80vh',
          width: '80vw',
          zIndex: 1,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      <Row className='align-items-center'>
        <Col>
          <h1 style={headingStyle}>Participant List</h1>
        </Col>

        {studyName && studyCode && (
          <Col className='text-right'>
            <h5>
              Study: {studyName} ({studyCode})
            </h5>
          </Col>
        )}
      </Row>

      {settingsSetOptions && settingsSetOptions.length > 0 && (
        <Row className='align-items-center'>
          <Col xs='auto'>
            <label
              style={{ marginRight: '8px', fontWeight: 'bold' }}
              htmlFor='participantSettingsSetSelect'
            >
              Settings set
            </label>
            <select
              id='participantSettingsSetSelect'
              value={effectiveSettingsSetId}
              onChange={(e) => handleSettingsSetChange(e.target.value)}
            >
              {settingsSetOptions.map((opt) => (
                <option key={opt._id} value={opt._id}>
                  {opt.name}
                  {opt.is_default ? ' (default)' : ''}
                  {opt.labels && opt.labels.length > 0
                    ? ` [${opt.labels.join(', ')}]`
                    : ''}
                </option>
              ))}
            </select>
          </Col>
        </Row>
      )}

      {allAccessList.find(
        (a) =>
          a.area === 'PARTICIPANT' &&
          a.modifier === 'CREATE' &&
          a.task === 'NEW_PARTICIPANT'
      ) && (
        <Row>
          <Col className='text-right'>
            <CreateParticipant
              studyId={studyId}
              settingsSetId={effectiveSettingsSetId || undefined}
            ></CreateParticipant>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card className='p-2 my-2 rounded'>
            <ParticipantFilters
              studyId={studyId}
              settingsSetId={selectedSettingsSetId}
              onFilterUpdated={handleFiltersUpdated}
              onStudyLoaded={onStudyLoaded}
            />
            {successGetList && (
              <Row>
                <Col>
                  {loadingDownloadTextResp ? (
                    <p>Loading...</p>
                  ) : (
                    <Button
                      variant='primary'
                      className='mt-2 mr-2'
                      onClick={() => downloadTextData()}
                    >
                      Download Text Responses
                    </Button>
                  )}
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      {errorGetList && <Message variant='danger'>{errorGetList}</Message>}
      {loadingGetList ? (
        <Loader />
      ) : (
        !logout &&
        participantsData &&
        participantsData.participants && (
          <>
            {participantsData.participants.length > 0 ? (
              <Table
                striped
                bordered
                hover
                responsive
                className='table-sm'
                style={{ marginTop: '20px' }}
              >
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>EMAIL</th>
                    {EMA_OR_EMI === 'ema' && <th>ENROLMENT STATUS</th>}
                    <th>ASSIGNED STAGE (#)</th>
                    <th>ASSIGNED GROUP (#)</th>
                    <th>{studyId === '63b756c6bb5dbb002a2a06a3' ? 'NEXT GROUP ASSIGNMENT PERFORMED AT' : 'DECISION ENGINE TOOLS'}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Particpants here are with participation filtered */}
                  {participantsData.participants.map((participant) => (
                    <tr key={participant._id}>
                      <td className='align-middle'>
                        {participant.participant_code}
                      </td>
                      <td className='align-middle'>{participant.email}</td>
                      {EMA_OR_EMI === 'ema' && (
                        <td className='align-middle'>
                          {participant.participation[0].status}
                        </td>
                      )}
                      <td className='align-middle'>
                        {participant.participation[0].current_stage.chin_name} (
                        {participant.participation[0].current_stage.stage_num})
                      </td>
                      <td className='align-middle'>
                        {participant.participation[0].current_group.name} (
                        {participant.participation[0].current_group.group_num})
                      </td>
                      <td className='align-middle'>
                        {studyId === '63b756c6bb5dbb002a2a06a3' ? (
                          getAutoGroupAssignmentDateTimeForCuhkCBTi(
                            participant.next_stage_scheduled_datetime
                          )
                        ) : (
                          successGetList && (
                            <>
                              <ActiveRulesViewer
                                email={participant.email}
                                studyId={studyId}
                                participantId={participant._id}
                              />
                              <EvaluateCustomImplementation
                                email={participant.email}
                                studyId={studyId}
                                participantId={participant._id}
                              />
                              <Last24hActivity
                                email={participant.email}
                                studyId={studyId}
                                participantId={participant._id}
                              />
                            </>
                          )
                        )}
                      </td>
                      <td className='align-middle'>
                        {successGetList && (
                          <AssignParticipant
                            enrolment={participant.participation[0]}
                            email={participant.email}
                            settingsSetId={effectiveSettingsSetId || undefined}
                            onSaveSuccess={onAssignParticipantSuccess}
                          ></AssignParticipant>
                        )}

                        {successGetList && studyId !== '63b756c6bb5dbb002a2a06a3' && (
                          <ScheduledJitaiTask
                            email={participant.email}
                            studyId={studyId}
                            participantId={participant._id}
                          ></ScheduledJitaiTask>
                        )}

                        {successGetList && studyId === '63b756c6bb5dbb002a2a06a3' && (
                          <ViewFitbitSleepLog
                            participantId={participant._id}
                          ></ViewFitbitSleepLog>
                        )}

                        {successGetList && studyId === '688ea9e42ccc1300218f2eef' && (
                          <>
                            <SmartWatchConnection
                              participantId={participant._id}
                              studyId={studyId}
                              initialSubjectId={
                                (participant.ami_device_metadata || []).find(
                                  (e) => String(e.study) === String(studyId)
                                )?.subject_id || ''
                              }
                              onConnectionChange={onAmiConnectionChange}
                            ></SmartWatchConnection>
                            <ScheduleSleepWakeTime
                              participantId={participant._id}
                              studyId={studyId}
                            ></ScheduleSleepWakeTime>
                          </>
                        )}

                        {successGetList && (
                          <ViewOptionalTreatments
                            enrolment={participant.participation[0]}
                            email={participant.email}
                          />
                        )}

                        {/*
                        // Do not support EMA in EMI study (for now)
                        successGetList && (
                          <EmaSchedule
                            email={participant.email}
                            studyId={participant.participation[0].study}
                            participantId={participant._id}
                          ></EmaSchedule>
                        )*/}

                        {/*
                        // As diary assessment shared by all stages in EMI studies (for now), it is not applicable here
                        successGetList &&
                          participant.participation[0].diary_ax_tasks.length >
                            0 && (
                            <DiaryAxTask
                              email={participant.email}
                              studyId={participant.participation[0].study}
                              participantId={participant._id}
                            ></DiaryAxTask>
                          )
                        */}

                        {/*
                        // Do not support image response for now
                        <S3ZipperDownload
                          participantId={participant._id}
                          studyId={studyId}
                          onDownloadError={(error) =>
                            setErrorDownloadImgResp(error)
                          }
                        ></S3ZipperDownload>*/}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Row className='align-items-center my-3'>
                <Col>
                  <h4>No participant is found</h4>
                </Col>
              </Row>
            )}
          </>
        )
      )}
    </>
  )
}

export default ParticipantListScreen
